import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { SupportedLanguage } from './routes'
import type { UiMessageKey } from './ui'

type I18nValue = {
  language: SupportedLanguage
  t: (key: UiMessageKey, options?: Record<string, unknown>) => string
}

const I18nContext = createContext<I18nValue | null>(null)

export function I18nProvider({ language, children }: { language: SupportedLanguage; children: ReactNode }) {
  const { i18n, t: translate } = useTranslation('common')

  useEffect(() => {
    document.documentElement.lang = language
    if (i18n.resolvedLanguage !== language) void i18n.changeLanguage(language)
  }, [i18n, language])

  const value = useMemo<I18nValue>(
    () => ({
      language,
      t: (key, options) => String(translate(key, { ...options, lng: language })),
    }),
    [language, translate],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n() {
  const value = useContext(I18nContext)
  if (!value) throw new Error('useI18n must be used within I18nProvider')
  return value
}
